import React, {useEffect, useState} from "react";
import {adminDeleteWorkerRequestAPI, adminWorkerRequestAPI, confirmWorkerRequestAPI} from "../../../services/admin";
import {WorkerRequestResponseData} from "../../../types/admin";
import styles from "./AdminWorkerRequestInfo.module.css";
import TextCard from "../../common/textCard/TextCard";
import Button from "../../common/button/Button";
import {RequestStatus} from "../../../types/request";
import ConfirmWorkerRequestPopUp from "../../../views/admin/popup/ConfirmWorkerRequestPopUp";
import {useAlert} from "../../common/error/AlertContext";
import {useNavigate} from "react-router-dom";

interface AdminWorkerRequestProps {
    workerRequestId: number;
}

//어드민 작업자 추가 요청 정보
const AdminWorkerRequestInfo: React.FC<AdminWorkerRequestProps> = ({workerRequestId}) => {
    const [data, setData] = useState<WorkerRequestResponseData>();
    const [loading, setLoading] = useState(true);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const {showConfirm} = useAlert();
    const {showAlert} = useAlert();
    const navigate = useNavigate();

    // Date 객체를 YYYY-MM-DD 문자열로 변환
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // 계약 종료일 계산 함수
    const calculateEndDate = (startedAt: Date, months: number): Date => {
        let year = startedAt.getFullYear();
        let month = startedAt.getMonth() + months + 1;

        if (month > 12) {
            year += Math.floor((month - 1) / 12);
            month = month % 12;
            if (month === 0) month = 12;
        }

        const endDate = new Date(year, month - 1, startedAt.getDate() - 1);
        const lastDayOfMonth = new Date(year, month - 1, 0).getDate();
        if (endDate.getDate() > lastDayOfMonth) {
            endDate.setDate(lastDayOfMonth);
        }
        return endDate;
    };

    //작업자 요청 처리
    const handleProcess = async (status: RequestStatus, period?: number, rejectComment?: string) => {
        try {
            let expireAtString: string | undefined;

            if (period && data?.startedAt) {
                // 시작일로부터 계약 기간만큼 계산
                const startDate = new Date(data.startedAt);
                const expireAtDate = calculateEndDate(startDate, period);
                expireAtString = formatDateToString(expireAtDate);
            }

            //작업자 요청 처리 API
            await confirmWorkerRequestAPI({
                workerRequestId,
                status,
                rejectComment,
                expireAt: expireAtString, // 문자열로 전송
                period
            });
            await fetchData();
            setIsPopupOpen(false);
        } catch (error) {
            console.log(error);
        }
    };

    //요청 삭제
    const handleDelete = async () => {
        showConfirm(
            "정말 삭제하시겠습니까?",
            async () => {
                try {
                    //삭제 API
                    await adminDeleteWorkerRequestAPI(workerRequestId);
                    showAlert('삭제되었습니다.', "info")
                    //삭제 후 테이블 페이지로  이동
                    navigate("/admin/billing");
                } catch (error) {
                    console.log(error);
                    showAlert('삭제 중 오류가 발생했습니다.', "error")
                }
            }
        );
    };

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //작업자 추가 요청 데이터 조회 API
            const response = await adminWorkerRequestAPI(workerRequestId);
            setData(response);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, [workerRequestId]);

    //날짜 요일
    const formatDate = (date: Date | undefined, day: number | undefined) => {
        if (!date || day === undefined) return '-';
        const dayOfWeek = ['월', '화', '수', '목', '금', '토', '일'][day - 1];
        return `${date}(${dayOfWeek})`;
    };

    //날짜 관련 데이터
    const dateInfo = [
        {
            label: "최초 요청일",
            value: formatDate(data?.createdAt, data?.createDay)
        },
        {
            label: "요청 처리일",
            value: formatDate(data?.confirmedAt, data?.confirmDay)
        },
        {
            label: "희망 작업 시작일",
            value: formatDate(data?.startedAt, data?.startDay)
        }
    ];

    //요청 관련 데이터
    const requestInfo = [
        {
            label: "요청 회사",
            value: data?.companyName || '-'
        },
        {
            label: "처리 상태",
            value: data?.requestStatus?.toString() || '-'
        },
        {
            label: "현재 작업자 인원",
            value: data?.currentAmount ? `${data.currentAmount}명` : '-'
        },
        {
            label: "희망 추가 인원",
            value: data?.amount ? `${data.amount.toLocaleString()}명` : '-'
        }
    ];

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    //어드민이 생성한 요청
    const isAdmin = data?.requestStatus === '어드민';

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <h2 className={styles.title}>Worker Request Information</h2>
                        <p className={styles.subtitle}>작업자 추가 요청 정보</p>
                    </div>
                </div>
                {/*요청 정보*/}
                <div className={styles.infoContainer}>
                    <TextCard items={dateInfo} className={styles.fullWidth}/>
                    <TextCard items={requestInfo} className={styles.fullWidth}/>
                    <div className={styles.noteContainer}>
                        <label className={styles.noteLabel}>비고</label>
                        <p className={styles.note}>{data?.note}</p>
                    </div>
                </div>
                {/*클라이언트가 생성한 요청인 경우*/}
                {!isAdmin ? (
                    <>
                        {/*안내 문구*/}
                        <p className={styles.notice}>승인 시 희망일 정각에 작업자가 자동 추가됩니다.</p>
                    </>
                ) : (
                    // 어드민이 생성한 요청인 경우
                    <>
                        {/*안내 문구*/}
                        <p className={styles.notice}>희망일 정각에 작업자가 자동 추가됩니다.</p>
                    </>
                )}
                <div className={styles.buttonContainer}>
                    {/*어드민이 생성한 요청인 경우*/}
                    {isAdmin ? (
                        // 삭제 버튼
                        <Button
                            variant="primary"
                            onClick={handleDelete}
                        >
                            삭제
                        </Button>
                    ) : (
                        // 클라이언트가 생성한 요청인 경우 처리 버튼
                        <Button
                            variant="primary"
                            onClick={() => setIsPopupOpen(true)}
                        >
                            처리
                        </Button>
                    )}
                    {/*처리 팝업*/}
                    <ConfirmWorkerRequestPopUp
                        isOpen={isPopupOpen}
                        onClose={() => setIsPopupOpen(false)}
                        workerRequestId={workerRequestId}
                        onConfirm={handleProcess}
                    />
                </div>
            </div>
        </div>
    )
}

export default AdminWorkerRequestInfo;